/**
 * MobileAuditScheduleList — recurring-audit schedule cards ( <lg ). Batch 6.3.
 * GET /audits-qa/schedule — same endpoint desktop uses. Card renders
 * `is_overdue` verbatim from the server (never calculated on mobile).
 * Manager CRUD flows through the same POST/PATCH/DELETE /audits-qa/schedule
 * endpoints via an inline dialog (dialog inherits Batch 1 mobile-safe
 * Dialog patch); care workers never see the CRUD buttons.
 */
import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, CalendarClock, MoreVertical,
  AlertOctagon, CheckCircle2, MapPin, Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const FREQUENCIES = ["weekly", "monthly", "quarterly", "six_monthly", "annual", "custom"];

export default function MobileAuditScheduleList({ canManage, refreshTick, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [sites, setSites] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/audits-qa/schedule");
      setRows(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);
  useEffect(() => {
    api.get("/audits-qa/templates", { params: { active_only: true } }).then((r) => setTemplates(r.data.items || [])).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
  }, []);

  const del = async (id) => {
    if (!window.confirm("Remove this scheduled audit?")) return;
    try { await api.delete(`/audits-qa/schedule/${id}`); toast.success("Schedule removed"); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const siteName = (id) => sites.find((s) => s.id === id)?.name;

  return (
    <div className="space-y-3" data-testid="mobile-audits-schedule">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500 tabular-nums" data-testid="mobile-schedule-count">
          {rows.length} scheduled audit{rows.length === 1 ? "" : "s"}
        </div>
        {canManage && (
          <Button size="sm" onClick={() => setCreating(true)} data-testid="btn-mobile-schedule-new"
            className="h-11 min-h-[44px] bg-sky-700 hover:bg-sky-800 text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /> Schedule Audit
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 p-4">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-center" data-testid="mobile-schedule-empty">
          <CalendarClock className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <div className="text-sm font-semibold text-slate-700">No scheduled audits.</div>
          <div className="text-[11px] text-slate-500 mt-1">Set up a recurring cadence to keep on top of your programme.</div>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="mobile-schedule-list">
          {rows.map((r) => (
            <li key={r.id} data-testid={`mobile-schedule-row-${r.id}`} className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="flex items-start gap-2">
                <div className="min-w-0 flex-1">
                  {/* Template / Category */}
                  <div className="text-sm font-semibold text-slate-900 truncate" data-testid={`mobile-schedule-title-${r.id}`}>
                    {r.template_name || r.category_label || "—"}
                  </div>
                  {r.template_name && (
                    <div className="text-[11px] text-slate-500 mt-0.5">{r.category_label || "—"}</div>
                  )}
                </div>
                {canManage && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`btn-mobile-schedule-kebab-${r.id}`}
                        className="min-h-[44px] w-11 h-11 -mt-1 -mr-1 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
                        aria-label="Schedule actions"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem onClick={() => setEditing(r)} data-testid={`btn-mobile-edit-schedule-${r.id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => del(r.id)} data-testid={`btn-mobile-delete-schedule-${r.id}`} className="text-red-600 focus:text-red-700">
                        <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>

              {/* Site · Frequency */}
              <div className="text-[11px] text-slate-600 mt-1 flex items-center gap-2 flex-wrap">
                <span className="flex items-center gap-1"><Building2 className="w-3 h-3 text-slate-400" /> {siteName(r.site_id) || "—"}</span>
                <span className="text-slate-400">·</span>
                <span className="capitalize" data-testid={`mobile-schedule-frequency-${r.id}`}>
                  {r.frequency?.replace(/_/g, " ") || "—"}
                </span>
              </div>

              {/* Responsible */}
              {r.responsible_name && (
                <div className="text-[11px] text-slate-600 mt-0.5 flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-slate-400" />
                  <span>{r.responsible_name}</span>
                </div>
              )}

              {/* Last completed · Next due */}
              <div className="text-[11px] text-slate-600 mt-1 flex items-center gap-2 flex-wrap tabular-nums">
                <span data-testid={`mobile-schedule-last-${r.id}`}><span className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mr-1">Last</span>{r.last_completed || "—"}</span>
                <span className="text-slate-400">·</span>
                <span data-testid={`mobile-schedule-next-${r.id}`}><span className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold mr-1">Next</span>{r.next_due_date || "—"}</span>
              </div>

              {/* Overdue chip — server authoritative (is_overdue) */}
              <div className="mt-1.5">
                {r.is_overdue ? (
                  <span data-testid={`mobile-schedule-status-${r.id}`} data-overdue="true"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border bg-red-100 text-red-800 border-red-300">
                    <AlertOctagon className="w-3 h-3" /> Overdue
                  </span>
                ) : (
                  <span data-testid={`mobile-schedule-status-${r.id}`} data-overdue="false"
                    className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300">
                    <CheckCircle2 className="w-3 h-3" /> On Track
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <ScheduleDialog
        open={creating || !!editing} schedule={editing} templates={templates} sites={sites}
        onOpenChange={(v) => { if (!v) { setEditing(null); setCreating(false); } }}
        onSaved={() => { setEditing(null); setCreating(false); load(); onChanged?.(); }}
      />
    </div>
  );
}

/** Same fields + same endpoints as the desktop ScheduleDialog. */
function ScheduleDialog({ open, schedule, templates, sites, onOpenChange, onSaved }) {
  const [form, setForm] = useState(defaults());
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm({ ...defaults(), ...(schedule || {}) }); }, [open, schedule]);
  const patch = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const pickTemplate = (id) => {
    patch("template_id", id || null);
    const t = templates.find((x) => x.id === id);
    if (t) { patch("template_name", t.name); patch("category", t.category); }
  };
  const save = async () => {
    if (!form.category) return toast.error("Category required");
    setSaving(true);
    try {
      if (schedule?.id) await api.patch(`/audits-qa/schedule/${schedule.id}`, form);
      else await api.post("/audits-qa/schedule", form);
      toast.success(schedule?.id ? "Schedule updated" : "Schedule created");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white" data-testid="mobile-schedule-dialog">
        <DialogHeader><DialogTitle>{schedule?.id ? "Edit Scheduled Audit" : "Schedule an Audit"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <FL label="Template">
              <select value={form.template_id || ""} onChange={(e) => pickTemplate(e.target.value)} data-testid="mobile-schedule-input-template"
                className="min-h-[44px] h-11 w-full text-sm border border-slate-300 rounded-md bg-white px-2">
                <option value="">— Choose a template —</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </FL>
          </div>
          <FL label="Frequency">
            <select value={form.frequency} onChange={(e) => patch("frequency", e.target.value)} data-testid="mobile-schedule-input-frequency"
              className="min-h-[44px] h-11 w-full text-sm border border-slate-300 rounded-md bg-white px-2 capitalize">
              {FREQUENCIES.map((f) => <option key={f} value={f}>{f.replace(/_/g, " ")}</option>)}
            </select>
          </FL>
          {form.frequency === "custom" && (
            <FL label="Every N days">
              <Input type="number" min="1" value={form.custom_days ?? ""} onChange={(e) => patch("custom_days", Number(e.target.value))} data-testid="mobile-schedule-input-custom" className="h-11" />
            </FL>
          )}
          <FL label="Site">
            <select value={form.site_id || ""} onChange={(e) => patch("site_id", e.target.value || null)} data-testid="mobile-schedule-input-site"
              className="min-h-[44px] h-11 w-full text-sm border border-slate-300 rounded-md bg-white px-2">
              <option value="">— Choose site —</option>
              {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </FL>
          <FL label="Responsible person">
            <Input value={form.responsible_name || ""} onChange={(e) => patch("responsible_name", e.target.value)} data-testid="mobile-schedule-input-responsible" className="h-11" />
          </FL>
          <FL label="Last completed">
            <Input type="date" value={form.last_completed || ""} onChange={(e) => patch("last_completed", e.target.value || null)} data-testid="mobile-schedule-input-last" className="h-11" />
          </FL>
          <FL label="Next due (optional)">
            <Input type="date" value={form.next_due_date || ""} onChange={(e) => patch("next_due_date", e.target.value || null)} data-testid="mobile-schedule-input-next" className="h-11" />
          </FL>
          <div className="md:col-span-2">
            <FL label="Notes"><Textarea rows={2} value={form.notes || ""} onChange={(e) => patch("notes", e.target.value)} /></FL>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-11">Cancel</Button>
          <Button onClick={save} disabled={saving} className="h-11 bg-sky-700 hover:bg-sky-800" data-testid="btn-mobile-schedule-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function defaults() {
  return { template_id: null, template_name: "", category: "other", frequency: "monthly",
    custom_days: null, site_id: null, responsible_name: "", last_completed: null,
    next_due_date: null, notes: "", is_active: true };
}
function FL({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  );
}
